/**
 * Фото сборщика на «Забрал»/«Вернул»: сжатие на телефоне и PUT в хранилище.
 *
 * Механика — та же, что у фото завершения поездки в Aida-PWA
 * (`src/features/ride/end/{downscale,uploadPhotoProof}.ts`), сжата до нужного
 * здесь: сервер выдаёт presigned PUT (JPEG ≤ 1 МБ), ТЗ просит ≤ 300 КБ.
 *
 * ⚠ PUT ИДЁТ МИМО `request()`, И ЭТО ТРЕБОВАНИЕ, А НЕ ЛЕНЬ. Адрес — чужой
 * origin хранилища: токен сборщика туда не отправляется никогда (S3 к тому же
 * отвечает 400 на второй способ авторизации рядом с подписью в URL), и путь
 * не склеивается с `API_BASE`. Адрес используется дословно — любая добавка
 * ломает подпись.
 *
 * ⚠ СТАТУС 0 — «ОБРЫВ СЕТИ ИЛИ CORS», и клиент их не различит. PUT всегда
 * идёт с preflight: если бакет не разрешает PUT/OPTIONS с origin приложения
 * (веб и Capacitor), каждая загрузка падает так же, как без сети. Это
 * настройка бакета, а не кода.
 */

/** Лимит ТЗ: «фото ≤ 300 КБ». */
export const PHOTO_MAX_BYTES = 300 * 1024

/** Длинная сторона до сжатия. Номер и замок на 1280 px читаются. */
const TARGET_LONG_SIDE_PX = 1280

/** Сначала теряем качество (дёшево для глаза), потом размер. */
const QUALITIES = [0.8, 0.65, 0.5] as const
const SCALES = [1, 0.75, 0.5, 0.35] as const

/**
 * Перебор ступеней: первая, что влезла в лимит. `null` — не влезло нигде или
 * кодировщик отказал (повторять тогда нечего: отказ не от размера).
 *
 * Кодировщик внедряется: в jsdom нет ни `createImageBitmap`, ни
 * `canvas.toBlob`, а порядок ступеней — то, что стоит проверять.
 */
export async function fitLadder(
  encode: (scale: number, quality: number) => Promise<Blob | null>,
  maxBytes: number,
): Promise<Blob | null> {
  for (const scale of SCALES) {
    for (const quality of QUALITIES) {
      const blob = await encode(scale, quality)
      if (blob === null) return null
      if (blob.size <= maxBytes) return blob
    }
  }
  return null
}

export class PhotoDecodeError extends Error {
  constructor() {
    super('photo decode failed')
    this.name = 'PhotoDecodeError'
  }
}

/**
 * Снимок камеры → JPEG ≤ `PHOTO_MAX_BYTES`.
 *
 * `imageOrientation: 'from-image'` — снимок с телефона лежит на боку, а
 * поворот записан в EXIF; без этого фото на проверке смотрело бы в сторону.
 */
export async function compressPhoto(file: Blob): Promise<Blob> {
  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
  } catch {
    throw new PhotoDecodeError()
  }
  const canvas = document.createElement('canvas')
  try {
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new PhotoDecodeError()
    const base = Math.min(1, TARGET_LONG_SIDE_PX / Math.max(bitmap.width, bitmap.height))
    const out = await fitLadder(async (scale, quality) => {
      canvas.width = Math.max(1, Math.round(bitmap.width * base * scale))
      canvas.height = Math.max(1, Math.round(bitmap.height * base * scale))
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
      return new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, 'image/jpeg', quality)
      })
    }, PHOTO_MAX_BYTES)
    if (!out) throw new PhotoDecodeError()
    return out
  } finally {
    bitmap.close()
    canvas.width = 0
    canvas.height = 0
  }
}

/** Отказ загрузки. Только код: presigned URL — сам по себе пропуск, в ошибку его не кладём. */
export class PhotoUploadError extends Error {
  constructor(readonly status: number) {
    super(`photo upload failed (status ${status})`)
    this.name = 'PhotoUploadError'
  }
}

/**
 * Заголовки, которыми подписан URL, кроме `host` (его ставит браузер).
 * Подписанный, но не отправленный заголовок — немой 403 SignatureDoesNotMatch.
 */
export function signedHeaderNames(uploadUrl: string): string[] {
  let signed: string | null
  try {
    signed = new URL(uploadUrl).searchParams.get('X-Amz-SignedHeaders')
  } catch {
    return []
  }
  return (signed ?? '')
    .split(';')
    .map((h) => h.trim().toLowerCase())
    .filter((h) => h !== '' && h !== 'host')
}

export async function putPhoto(uploadUrl: string, blob: Blob): Promise<void> {
  const headers: Record<string, string> = {}
  for (const name of signedHeaderNames(uploadUrl)) {
    headers[name] = name === 'content-type' ? blob.type : ''
  }
  let res: Response
  try {
    res = await fetch(uploadUrl, { method: 'PUT', body: blob, headers, credentials: 'omit' })
  } catch {
    throw new PhotoUploadError(0)
  }
  if (!res.ok) throw new PhotoUploadError(res.status)
}
